import { useEffect, useState } from "react";
import { formatDateInput, parseDateInput } from "../../utils/date";
import { FiEdit } from "react-icons/fi";

export interface TaskField {
    name: string;
    label: string;
    type: "text" | "number" | "date" | "textarea";
    required?: boolean;
}

interface TaskFormProps<T> {
    title: string;
    isNew: boolean;
    task?: T;
    fields: TaskField[];
    onSubmit: (data: T) => Promise<void> | void;
    defaultState: T;
    readOnly?: boolean;
}

export default function TaskForm<T extends Record<string, any>>(props: TaskFormProps<T>) {
    const { title, isNew, task, fields, onSubmit, defaultState, readOnly } = props;

    const [formData, setFormData] = useState<T>(defaultState);
    const [editing, setEditing] = useState<boolean>(isNew);
    const [saving, setSaving] = useState<boolean>(false);

    useEffect(() => {
        if (task) {
            const newState: any = {};
            for (const key in defaultState) {
                newState[key] = key === "date" ? new Date(task[key]) : task[key] ?? defaultState[key];
            }
            setFormData(newState);
        }
    }, [task]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value ,type} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === "date" ? parseDateInput(value) : value
        }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        Promise.resolve(onSubmit(formData))
            .then(() => {
                setSaving(false);
                setFormData(defaultState);
                setEditing(false);
            })
            .catch(() => {
                setSaving(false);
            });
    };

    return (
        <div className="bg-white p-5 rounded-lg shadow-md min-w-[400px] min-h-[500px]
             max-[480px]:min-w-[90%] max-[480px]:p-1.5">
            <div className="flex items-center justify-between pb-3">
                <h1 className="text-xl font-bold text-gray-800">{title}</h1>
                {!isNew && !editing && !readOnly && (
                    <button type="button" className="icon-span" onClick={() => setEditing(true)}>
                        <FiEdit size={22} />
                    </button>
                )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
                {fields.map(field => (
                    <div key={field.name} className="flex flex-col">
                        <label className="text-sm font-semibold text-gray-700 mb-1">
                            {field.label}
                        </label>

                        {field.type === "textarea" ? (
                            <textarea
                                name={field.name}
                                value={formData[field.name] || ""}
                                onChange={handleChange}
                                readOnly={!isNew && (!editing || readOnly)}
                                disabled={saving}
                                className="p-2 w-full rounded-lg bg-gray-50 border border-gray-200 h-[100px]"
                            />
                        ) : (
                            <input
                                type={field.type}
                                name={field.name}
                                value={
                                    formData[field.name] instanceof Date
                                        ? formatDateInput(formData[field.name])
                                        : formData[field.name]
                                }
                                onChange={handleChange}
                                readOnly={!isNew && (!editing || readOnly)}
                                disabled={saving}
                                required={field.required}
                                className="py-1.5 px-3 w-full rounded-lg bg-gray-50 border border-gray-200"
                            />
                        )}
                    </div>
                ))}

                {(isNew || (editing && !readOnly)) && (
                    <button className="btn pt-3 rounded-lg" type="submit" disabled={saving}>
                        {saving ? "Speichern..." : (isNew ? "Hinzufügen" : "Speichern")}
                    </button>
                )}
            </form>
        </div>
    );
}
